import React from 'react';
import {Box} from '@mui/material';
import {Link} from 'react-router-dom';
import {CARTE, CIBLE_REMPLISSAGE, NAVY, TEXTE, TITRE_SECTION} from '../../tokens.js';
import Courbe from '../Courbe.js';
import FicheMiniCarte from './FicheMiniCarte.js';
import {BadgeFlux, BadgeQuantite, BadgeTypologie} from './FicheBadges.js';
import {GROUPE_LIBELLES, STATUT_LIBELLES, formatDate} from './fiche.selectors.js';

const carteSx = {bgcolor: CARTE.fond, border: `1px solid ${CARTE.bordure}`, borderRadius: `${CARTE.radius}px`, boxShadow: CARTE.ombre, overflow: 'hidden'};
const dtSx = {color: TITRE_SECTION};
const ddSx = {m: 0, textAlign: 'right', fontWeight: 600, color: TEXTE};

function Detail({libelle, valeur, mono, sous}) {
    return (
        <>
            <Box component="dt" sx={dtSx}>{libelle}</Box>
            <Box component="dd" sx={{...ddSx, ...(mono ? {fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: NAVY} : {})}}>
                {valeur}
                {sous && <Box component="span" sx={{display: 'block', fontSize: 11, fontWeight: 400, color: TITRE_SECTION}}>{sous}</Box>}
            </Box>
        </>
    );
}

function Section({titre, children}) {
    return (
        <Box component="section" sx={{...carteSx, px: '20px', py: '16px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
            <Box component="h2" sx={{m: 0, fontSize: 11, fontWeight: 700, letterSpacing: '.14em', textTransform: 'uppercase', color: TITRE_SECTION}}>{titre}</Box>
            <Box component="dl" sx={{m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '8px 14px', fontSize: 13}}>{children}</Box>
        </Box>
    );
}

/** Courbe pleine largeur du bloc (sous la grille de détails), avec sa légende. */
function Evolution({legende, children}) {
    return (
        <Box sx={{gridColumn: '1 / -1', pt: '6px', borderTop: `1px solid ${CARTE.separateur}`, display: 'flex', flexDirection: 'column', gap: '4px'}}>
            <Box component="span" sx={{fontSize: 11, color: TITRE_SECTION}}>{legende}</Box>
            {children}
        </Box>
    );
}

const nombre = (n, d = 0) => n.toLocaleString('fr-FR', {maximumFractionDigits: d});
/** Litres ; au-delà de 10 000 L, m³ à 1 décimale. */
const formatLitres = (l) => (l > 10000 ? `${nombre(l / 1000, 1)} m³` : `${nombre(Math.round(l))} L`);
/** kg sous 1 000, sinon tonnes (1 décimale). */
const formatKg = (kg) => (kg >= 1000 ? `${nombre(kg / 1000, 1)} t` : `${nombre(Math.round(kg))} kg`);
const formatPct = (t) => `${nombre(Math.round(t * 100))} %`;

/**
 * Rail de contexte (R4, 340 px). Les consignes actives ne figurent pas dans le snapshot : le bloc est absent (pas vide).
 * `lavage` (`statsLavage`) : bloc absent si `null`. `collecte` (`statsCollecte`, lot 8) : bloc absent si `null` (bouton à 0 ou aucune collecte).
 */
export default function FicheRail({pav, patrimoine, couleurMarqueur, carteTo, lavage = null, collecte = null, courbeLavages = [], courbeCollectes = []}) {
    const adresse = pav.adresse && pav.adresse.trim() ? pav.adresse.trim() : '—';
    const commune = pav.communeLibelle ? pav.communeLibelle.toUpperCase() : '—';
    const point = pav.lat != null && pav.lng != null ? `${pav.lat.toFixed(5)}, ${pav.lng.toFixed(5)}` : '—';
    return (
        <Box component="aside" aria-label="Contexte du point" sx={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
            <Box component="section" sx={carteSx}>
                <FicheMiniCarte lat={pav.lat} lng={pav.lng} couleur={couleurMarqueur} etiquette={`Carte du point ${pav.nomenclature}`}/>
                <Box sx={{px: '20px', pt: '16px', pb: '18px', display: 'flex', flexDirection: 'column', gap: '10px'}}>
                    <Box sx={{display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: '8px'}}>
                        <Box component="span" sx={{fontSize: 20, fontWeight: 800, color: NAVY}}>{pav.nomenclature}</Box>
                        <Box component={Link} to={carteTo} sx={{fontSize: 12, fontWeight: 600, color: NAVY}}>Ouvrir la carte ↗</Box>
                    </Box>
                    <Box sx={{display: 'flex', gap: '6px', flexWrap: 'wrap'}}>
                        <BadgeFlux flux={pav.flux}/>
                        <BadgeTypologie typologie={pav.typologie}/>
                        <BadgeQuantite quantite={pav.quantite}/>
                    </Box>
                    <Box component="span" sx={{fontSize: 13, color: TEXTE}}>{adresse} · {commune}</Box>
                    <Box component="dl" sx={{m: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 14px', fontSize: 12, pt: '8px', borderTop: `1px solid ${CARTE.separateur}`}}>
                        <Detail libelle="Point" valeur={point}/>
                        <Detail libelle="Patrimoine" valeur={patrimoine ? (GROUPE_LIBELLES[patrimoine.groupe] || patrimoine.libelle) : '—'}/>
                        <Detail libelle="Circuit" valeur={(patrimoine && patrimoine.libelle) || '—'}/>
                        <Detail libelle="Commune" valeur={pav.communeLibelle || '—'}/>
                    </Box>
                </Box>
            </Box>

            {lavage && (
                <Section titre={`Lavage · ${lavage.nb} passage${lavage.nb > 1 ? 's' : ''}`}>
                    <Detail libelle="Dernier lavage" valeur={`${formatDate(lavage.dernier.date)} · ${lavage.dernier.litres != null ? formatLitres(lavage.dernier.litres) : '—'}`}/>
                    <Detail libelle="Eau moyenne" valeur={lavage.eauMoyenne != null ? `≈ ${formatLitres(lavage.eauMoyenne)}` : '—'}/>
                    <Detail libelle="Eau cumulée" valeur={lavage.eauCumulee != null ? `≈ ${formatLitres(lavage.eauCumulee)}` : '—'}
                            sous={lavage.nbAvecConso < lavage.nb ? `sur ${lavage.nbAvecConso}/${lavage.nb} lavages` : null}/>
                    <Evolution legende={`${courbeLavages.length} dernier${courbeLavages.length > 1 ? 's' : ''} lavage${courbeLavages.length > 1 ? 's' : ''} · litres`}>
                        <Courbe points={courbeLavages} hauteur={48} format={formatLitres}/>
                    </Evolution>
                </Section>
            )}

            {collecte && (
                <Section titre={`Collecte · ${collecte.nb} collecte${collecte.nb > 1 ? 's' : ''}${collecte.nbDeclasses > 0 ? ` · ${collecte.nbDeclasses} déclassée${collecte.nbDeclasses > 1 ? 's' : ''}` : ''}`}>
                    <Detail libelle="Dernière collecte" valeur={collecte.derniere.declassee
                        ? `${formatDate(collecte.derniere.date)} · déclassée`
                        : [formatDate(collecte.derniere.date), collecte.derniere.kg != null ? formatKg(collecte.derniere.kg) : null, collecte.derniere.taux != null ? formatPct(collecte.derniere.taux) : null].filter(Boolean).join(' · ')}/>
                    <Detail libelle="Tonnage cumulé" valeur={formatKg(collecte.tonnageCumule)}/>
                    <Detail libelle="Tonnage moyen par bac" valeur={collecte.tonnageMoyen != null ? formatKg(collecte.tonnageMoyen) : '—'}/>
                    <Detail libelle="Remplissage moyen" valeur={collecte.remplissageMoyen != null ? formatPct(collecte.remplissageMoyen) : '—'}/>
                    <Evolution legende={`${courbeCollectes.length} dernière${courbeCollectes.length > 1 ? 's' : ''} collecte${courbeCollectes.length > 1 ? 's' : ''} · remplissage, cible ${formatPct(CIBLE_REMPLISSAGE)}`}>
                        <Courbe points={courbeCollectes} hauteur={48} couleur="#7A4B2A" format={formatPct} cible={CIBLE_REMPLISSAGE}/>
                    </Evolution>
                </Section>
            )}

            <Section titre="Point">
                <Detail libelle="Statut" valeur={STATUT_LIBELLES[pav.statut] || pav.statut || '—'}/>
                <Detail libelle="Identifiant" valeur={pav.nomenclature} mono/>
            </Section>
        </Box>
    );
}
